import React from 'react';
import styled from 'styled-components';
import { useParams, Link, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';
import { jobs } from '../data/jobs';

const renderInline = (text: string): { __html: string } => ({
  __html: text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
});

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({theme}) => theme.colors.primary};
  text-decoration: none;
  margin-bottom: 30px;
  font-size: 16px;
  transition: color 0.3s;
  
  svg {
    transition: transform 0.3s;
  }
  
  &:hover {
    color: ${({theme}) => theme.colors.secondary};
    
    svg {
      transform: translateX(-3px);
    }
  }
`;

const JobHeader = styled.div`
  margin-bottom: 40px;
`;

const JobTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  line-height: 1.2;
  
  ${mediaQueries.medium} {
    font-size: 30px;
  }
  
  ${mediaQueries.small} {
    font-size: 30px;
  }
`;

const JobHighlight = styled.span`
  color: ${({theme}) => theme.colors.secondary};
`;

const JobMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 30px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #333;
  
  svg {
    color: ${({theme}) => theme.colors.secondary};
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px 0 32px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-right: 24px;
`;

const ChipGroupTitle = styled.h3`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
`;

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  
  &.backend {
    background-color: #E3F2FD;
    color: #0D47A1;
  }
  
  &.frontend {
    background-color: #FFF8E1;
    color: #FF8F00;
  }
  
  &.devops {
    background-color: #E8F5E9;
    color: #2E7D32;
  }
  
  &.tools {
    background-color: #F3E5F5;
    color: #7B1FA2;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 100px 0 20px;
  color: ${({theme}) => theme.colors.primary};
  
  ${mediaQueries.medium} {
    font-size: 22px;
    margin: 70px 0 20px;

  }
`;

const ListContainer = styled.ul`
  padding-left: 20px;
  margin-bottom: 30px;
  
  strong {
    color: ${({theme}) => theme.colors.lightPrimary};
    font-weight: 600;
  }
`;

const ListItem = styled.li`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 12px;
  position: relative;
  
  &::before {
    content: "";
    position: absolute;
    left: -20px;
    top: 10px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({theme}) => theme.colors.lightPrimary};
  }
  
  ${mediaQueries.medium} {
    font-size: 16px;
  }
`;

const ApplySection = styled.section`
  background-color: #f8f8f8;
  border-radius: 16px;
  padding: 40px;
  margin-top: 60px;
  text-align: center;
  
  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const ApplyTitle = styled.h2`
  font-size: 28px;
  color: ${({theme}) => theme.colors.primary};
  margin-bottom: 20px;
  
  ${mediaQueries.medium} {
    font-size: 24px;
  }
`;

const ApplyText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  ${mediaQueries.medium} {
    font-size: 16px;
  }
`;

const ApplyButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 32px;
  border-radius: 32px;
  text-decoration: none;
  font-weight: 600;
  display: inline-block;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const JobIntro = styled.p`
  font-size: 20px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({theme}) => theme.colors.primary};
  margin: 0 0 28px;

  ${mediaQueries.medium} {
    font-size: 18px;
  }
`;

const EssentialsBox = styled.dl`
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  margin: 0 0 48px;
  padding: 0;
  overflow: hidden;
`;

const EssentialsRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  border-bottom: 1px solid #e8e8e8;

  &:last-child {
    border-bottom: none;
  }

  ${mediaQueries.small} {
    grid-template-columns: 1fr;
  }
`;

const EssentialsTerm = styled.dt`
  font-weight: 600;
  font-size: 14px;
  color: #555;
  padding: 14px 18px;
  background: #f8f9fa;
  border-right: 1px solid #e8e8e8;
  margin: 0;

  ${mediaQueries.small} {
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
    padding-bottom: 6px;
  }
`;

const EssentialsValue = styled.dd`
  font-size: 15px;
  color: #1a1a1a;
  padding: 14px 18px;
  margin: 0;
  line-height: 1.5;
`;

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const job = jobs.find(j => j.id === id);

  if (!job) {
    return <Navigate to="/recrutement" replace />;
  }

  const locationShort = job.location.split('—')[0].trim();

  return (
    <PageContainer>
      <SEO
        title={job.seoTitle}
        description={job.seoDescription}
        canonical={`${window.location.origin}/job/${id}`}
      />
      <Header backgroundColor="transparent" textColor="#011D3A" />

      <MainSection>
        <BackLink to="/recrutement">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          Retour aux offres
        </BackLink>

        <JobHeader>
          <JobTitle>
            <JobHighlight>{job.title}</JobHighlight>
          </JobTitle>

          <JobIntro dangerouslySetInnerHTML={renderInline(job.intro)} />

          <JobMeta>
            <MetaItem>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              {locationShort}
            </MetaItem>

            <MetaItem>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
              </svg>
              {job.type}
            </MetaItem>

            <MetaItem>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/>
              </svg>
              {job.startDate}
            </MetaItem>
          </JobMeta>
        </JobHeader>

        <SectionTitle>L'essentiel en un coup d'œil</SectionTitle>
        <EssentialsBox>
          <EssentialsRow>
            <EssentialsTerm>Contrat</EssentialsTerm>
            <EssentialsValue>{job.contract}</EssentialsValue>
          </EssentialsRow>
          <EssentialsRow>
            <EssentialsTerm>Niveau</EssentialsTerm>
            <EssentialsValue>{job.level}</EssentialsValue>
          </EssentialsRow>
          <EssentialsRow>
            <EssentialsTerm>Lieu</EssentialsTerm>
            <EssentialsValue>{job.location}</EssentialsValue>
          </EssentialsRow>
          <EssentialsRow>
            <EssentialsTerm>Démarrage</EssentialsTerm>
            <EssentialsValue>{job.startDate}</EssentialsValue>
          </EssentialsRow>
          <EssentialsRow>
            <EssentialsTerm>Rémunération</EssentialsTerm>
            <EssentialsValue>{job.salary}</EssentialsValue>
          </EssentialsRow>
          <EssentialsRow>
            <EssentialsTerm>Avantages</EssentialsTerm>
            <EssentialsValue>{job.benefits}</EssentialsValue>
          </EssentialsRow>
        </EssentialsBox>

        <SectionTitle>Tes missions concrètes</SectionTitle>
        <ListContainer>
          {job.missions.map((item, i) => (
            <ListItem key={i} dangerouslySetInnerHTML={renderInline(item)} />
          ))}
        </ListContainer>

        <SectionTitle>Ton quotidien chez Thinkfab</SectionTitle>
        <ListContainer>
          {job.dailyLife.map((item, i) => (
            <ListItem key={i} dangerouslySetInnerHTML={renderInline(item)} />
          ))}
        </ListContainer>

        {job.techStack && job.techStack.length > 0 && (
          <>
            <SectionTitle>Environnement technique</SectionTitle>
            <ChipsContainer>
              {job.techStack.map(group => (
                <ChipGroup key={group.label}>
                  <ChipGroupTitle>{group.label}</ChipGroupTitle>
                  <ChipsWrapper>
                    {group.chips.map(chip => (
                      <Chip key={chip} className={group.type}>{chip}</Chip>
                    ))}
                  </ChipsWrapper>
                </ChipGroup>
              ))}
            </ChipsContainer>
          </>
        )}

        <SectionTitle>Ce que tu apportes</SectionTitle>
        <ListContainer>
          {job.profile.map((item, i) => (
            <ListItem key={i} dangerouslySetInnerHTML={renderInline(item)} />
          ))}
        </ListContainer>

        <SectionTitle>Pourquoi tu vas aimer travailler ici</SectionTitle>
        <ListContainer>
          {job.perks.map((item, i) => (
            <ListItem key={i} dangerouslySetInnerHTML={renderInline(item)} />
          ))}
        </ListContainer>

        {job.applyUrl && (
          <ApplySection>
            <ApplyTitle>Intéressé·e par ce poste ?</ApplyTitle>
            <ApplyText>
              Envoyez-nous votre CV et une brève présentation expliquant pourquoi ce rôle te parle et ce que tu pourrais apporter à l'équipe.
            </ApplyText>
            <ApplyButton href={job.applyUrl} target="_blank" rel="noopener noreferrer">
              Postuler maintenant
            </ApplyButton>
          </ApplySection>
        )}
      </MainSection>

      <Footer
        heading="Rejoignez notre équipe !"
        subheading="Postulez dès maintenant et commencez une nouvelle aventure"
        buttonText="Envoyer votre candidature"
      />
    </PageContainer>
  );
};

export default JobDetail;